/* eslint-disable @next/next/no-img-element */
import * as React from 'react';
import { withOnLinkClickTracking } from 'auto-tracking';
import {
  Button,
  css,
  ExpandCollapseLottie,
  Icon,
  List,
  ListItem,
  ListItemButton,
  ListItemLabel,
  ListItemLeft,
  Popover,
  rem,
  SignOutIcon,
  styled,
  theme,
  UserIcon,
} from 'design-system';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  AuthScreen,
  useAuthContext,
  UserDialog,
} from 'onelogin-sso-password-grant';
import { useState } from 'react';
import { useCookies } from 'react-cookie';

import { useEventTracking } from '../contexts/tracking-context';
import { Journal } from '../resources/journal';
import { Container, SectionNew } from './section';

type StyleProps = {
  backgroundColor: string;
};

const HeaderSectionInner = styled(Container, {
  display: 'flex',
  height: '80px',
  gap: 8,
  alignItems: 'end',
  flexWrap: 'wrap',
  alignContent: 'center',
});
const SettingsContainer = styled('div', {
  marginLeft: 'auto',
  display: 'flex',
  gap: 8,
  alignItems: 'end',
});
const MenuContainer = styled('div', {
  paddingTop: rem(8),
  paddingBottom: rem(8),
});

const MaxWidthUsername = styled('span', {
  overflow: 'hidden',
  maxWidth: 180,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  display: 'block',
});
const ALink = withOnLinkClickTracking(styled('a', {}));
const UserSettings = () => {
  const c = useTranslation('common').t;
  const { authState } = useAuthContext();
  const user = authState.name === 'logged in' ? authState.user : undefined;
  const loading =
    authState.name === 'checking if logged in' ||
    authState.name === 'logging in';
  const [open, setOpen] = useState(false);
  const actualOpen = (user && open) ?? false;
  const { trackEvent } = useEventTracking();
  const [openDialog, setOpenDialog] = useState(false);
  const [screen, setScreen] = useState<AuthScreen>('default');

  if (user) {
    return (
      <>
        <Button
          id="Past Preflight Checks"
          href={'/user/history/version1'}
          size={'small'}
          color={'secondary'}
          css={{ textDecoration: 'none' }}
          aria-label="Past Preflight checks"
        >
          Past Preflight checks
        </Button>
        <Popover.Root open={actualOpen} onOpenChange={setOpen}>
          <Popover.Anchor>
            <Button
              color={'tertiary'}
              size={'small'}
              leftIcon={<UserIcon width={'14'} />}
              onClick={() => setOpen(!open)}
              rightIcon={
                <ExpandCollapseLottie
                  color={'dark'}
                  state={open ? 'expand' : 'collapse'}
                />
              }
              aria-label="User settings"
            >
              <MaxWidthUsername>{user.name}</MaxWidthUsername>
            </Button>
          </Popover.Anchor>
          <Popover.Popup css={{ minWidth: 240 }} align={'end'}>
            <MenuContainer>
              <List>
                <ListItem>
                  <Link passHref href={'/user/history/version1'}>
                    <ListItemButton as={'a'} aria-label="Your history">
                      <ListItemLeft>
                        <Icon
                          name={'arrowRotateLeft'}
                          label={'History'}
                          variant={'regular'}
                          width={'14'}
                        />
                      </ListItemLeft>
                      <ListItemLabel>Your history</ListItemLabel>
                    </ListItemButton>
                  </Link>
                </ListItem>
                <ListItem>
                  <ListItemButton
                    onClick={() => {
                      authState.logOut();
                      setOpen(false);
                      trackEvent(['Header', 'Logout_button', 'logout']);
                    }}
                    loading={loading}
                    aria-label="Log out"
                  >
                    <ListItemLeft>
                      <SignOutIcon width={'14'} />
                    </ListItemLeft>
                    <ListItemLabel>Log out</ListItemLabel>
                  </ListItemButton>
                </ListItem>
              </List>
            </MenuContainer>
          </Popover.Popup>
        </Popover.Root>
      </>
    );
  }
  const optionalLanguageTextUserDialog = {
    create_a_free_paperpal_account: c(
      'sign_in_page.create_a_free_paperpal_account',
      'Create a free Paperpal account',
    ),
    iso_iec_certified_data_security: c(
      'sign_in_page.iso_iec_certified_data_security',
      'ISO/IEC 27001:2013 certified data security protocols that protect your work',
    ),
    powerful_ai_technology: c(
      'sign_in_page.powerful_ai_technology',
      'Powerful AI technology to elevate your research writing',
    ),
    one_set_of_credentials: c(
      'sign_in_page.one_set_of_credentials',
      'One set of credentials to access the full product suite',
    ),
    if_you_have_any_questions: c(
      'sign_in_page.if_you_have_any_questions',
      `If you have any questions contact us at&nbsp;
                <a href={'mailto:hello@paperpal.com'}>hello@paperpal.com</a>`,
    ),
  };
  return (
    <>
      <UserDialog
        open={openDialog}
        setOpen={setOpenDialog}
        setScreen={setScreen}
        screen={screen}
        onSignIn={() => setOpenDialog(false)}
        optionalLanguageTextUserDialog={optionalLanguageTextUserDialog}
      />
      <Button
        id="Sign in"
        onClick={() => {
          setScreen('login');
          setOpenDialog(true);
          trackEvent(['Header', 'Login_button', 'Log in']);
        }}
        size={'small'}
        color={'tertiary'}
        aria-label="Sign in"
      >
        {c('header.sign_in', 'Sign in')}
      </Button>
    </>
  );
};

export const HeaderHomePage = ({
  journal,
  styleProps,
  onLogoClick,
  pageType,
}: {
  journal?: Journal;
  onLogoClick: () => void;
  styleProps?: StyleProps;
  pageType?: string;
}) => {
  const c = useTranslation('common').t;
  const showLogo =
    journal !== undefined &&
    journal.publisher.slug !== 'paperpal' &&
    journal.publisher.slug !== 'gjd-journal-finder' &&
    journal.publisher.slug !== 'editage-cn' &&
    journal.publisher.slug !== 'paperpal-preflight';

  const isGJDPage = journal?.publisher.slug === 'gjd-journal-finder';

  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <SectionNew css={{ padding: '0', border: '1px solid #E4EAF7' }}>
      <Container
        css={{
          backgroundColor: styleProps?.backgroundColor || theme.colors.white,
          padding: '0',
        }}
      >
        <HeaderSectionInner as="header">
          {isGJDPage ? (
            <img
              src="https://cdn.paperpal.com/assets/images/paperpal_editage_logo.webp"
              width={200}
              height={60}
              alt="Paperpal by Editage Logo"
              className={preflightLogo2()}
              onClick={onLogoClick}
              aria-label="Paperpal by Editage Logo"
            />
          ) : showLogo ? (
            pageType === 'publisher' &&
            journal.publisher.slug !== 'refworks' &&
            journal.publisher.slug !== 'endnote' &&
            journal.publisher.slug !== 'endnote-fusion' ? (
              journal.publisher.slug === 'ieee' ? (
                <>
                  <Image
                    src="/PreflightLogoNew.svg"
                    width={400}
                    height={100}
                    alt="Preflight Logo"
                    className={preflightLogo()}
                    onClick={onLogoClick}
                    aria-label="Preflight Logo"
                  />

                  <div
                    className={css({
                      display: 'flex',
                      alignItems: 'end',
                    })()}
                  >
                    <span
                      className={css({
                        fontSize: '14px',
                        lineHeight: '11px',
                        fontWeight: '400',
                        color: theme.colors.gray11,
                        marginRight: 5,
                      })()}
                    >
                      for
                    </span>

                    <JournalTitle
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      {truncateTitle(journal.publisher.label || '', 15)}
                      {showTooltip &&
                      journal.publisher.label &&
                      journal.publisher.label.length > 15 ? (
                        <Tooltip>{journal.publisher.label}</Tooltip>
                      ) : (
                        <></>
                      )}
                    </JournalTitle>
                  </div>
                </>
              ) : (
                <>
                  {journal.publisher.logo ? (
                    <Image
                      width={100}
                      height={20}
                      src={journal.publisher.logo ?? ''}
                      alt={journal.publisher.label ?? ''}
                      className={preflightJournalLogo()}
                      aria-label={journal.publisher.label}
                    />
                  ) : (
                    <JournalTitle
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      {truncateTitle(journal.publisher.label || '', 15)}
                      {showTooltip &&
                      journal.publisher.label &&
                      journal.publisher.label.length > 15 ? (
                        <Tooltip>{journal.publisher.label}</Tooltip>
                      ) : (
                        <></>
                      )}
                    </JournalTitle>
                  )}
                  <div
                    className={css({
                      display: 'flex',
                      gap: '4px',
                    })()}
                  >
                    <div
                      className={css({
                        fontSize: '14px',
                        lineHeight: '11px',
                        fontWeight: '400',
                        color: theme.colors.gray11,
                        marginRight: 5,
                        display: 'flex',
                        alignItems: 'end',
                      })()}
                    >
                      {c(
                        'header.in_collaboration_with',
                        'in collaboration with',
                      )}
                    </div>

                    <Image
                      src="/PreflightLogoNew.svg"
                      width={400}
                      height={100}
                      alt="Preflight Logo"
                      className={preflightLogo()}
                      onClick={onLogoClick}
                      aria-label="Preflight Logo"
                    />
                  </div>
                </>
              )
            ) : journal.publisher.slug === 'ieee' ? (
              <>
                <Image
                  src="/PreflightLogoNew.svg"
                  width={400}
                  height={100}
                  alt="Preflight Logo"
                  className={preflightLogo()}
                  onClick={onLogoClick}
                  aria-label="Preflight Logo"
                />

                <div
                  className={css({
                    display: 'flex',
                    alignItems: 'end',
                  })()}
                >
                  <span
                    className={css({
                      fontSize: '14px',
                      lineHeight: '11px',
                      fontWeight: '400',
                      color: theme.colors.gray11,
                      marginRight: 5,
                    })()}
                  >
                    for
                  </span>

                  <JournalTitle
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    {truncateTitle(journal.label || '', 15)}
                    {showTooltip && journal.label.length > 15 ? (
                      <Tooltip>{journal.label}</Tooltip>
                    ) : (
                      <></>
                    )}
                  </JournalTitle>
                </div>
              </>
            ) : (
              <>
                {journal.publisher.slug === 'endnote' ? (
                  <Image
                    src="/endnote.svg"
                    width={400}
                    height={100}
                    alt="Endnote Logo"
                    className={preflightJournalLogo()}
                    onClick={onLogoClick}
                    aria-label="Endnote Logo"
                  />
                ) : journal.publisher.slug === 'refworks' ? (
                  <ALink
                    href="https://refworks.proquest.com/learn-more/"
                    data-test-id="refworks-logo-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Refworks Logo"
                  >
                    <Image
                      src="/refworks.svg"
                      width={400}
                      height={100}
                      alt="refworksLogo"
                      className={preflightJournalLogo()}
                      onClick={onLogoClick}
                    />
                  </ALink>
                ) : journal.publisher.slug === 'endnote-fusion' ? (
                  //endnote fusion logo
                  <Image
                    src="/endnote-fusion.svg"
                    width={400}
                    height={100}
                    alt="Endnote Fusion Logo"
                    className={preflightJournalLogo()}
                    onClick={onLogoClick}
                    aria-label="Endnote Fusion Logo"
                  />
                ) : journal.logo ? (
                  <Image
                    width={100}
                    height={20}
                    src={journal.logo ?? ''}
                    alt={journal.label}
                    className={preflightJournalLogo()}
                    aria-label={journal.label}
                  />
                ) : (
                  <JournalTitle
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    {truncateTitle(journal.label || '', 15)}
                    {showTooltip && journal.label.length > 15 ? (
                      <Tooltip>{journal.label}</Tooltip>
                    ) : (
                      <></>
                    )}
                  </JournalTitle>
                )}
                <div
                  className={css({
                    display: 'flex',
                    gap: '4px',
                  })()}
                >
                  <div
                    className={css({
                      fontSize: '14px',
                      lineHeight: '11px',
                      fontWeight: '400',
                      color: theme.colors.gray11,
                      marginRight: 5,
                      display: 'flex',
                      alignItems: 'end',
                    })()}
                  >
                    {c('header.in_collaboration_with', 'in collaboration with')}
                  </div>

                  <Image
                    src="/PreflightLogoNew.svg"
                    width={400}
                    height={100}
                    alt="Preflight Logo"
                    className={preflightLogo()}
                    onClick={onLogoClick}
                    aria-label="Preflight Logo"
                  />
                </div>
              </>
            )
          ) : (
            <>
              <Image
                src="/PreflightLogoNew.svg"
                width={400}
                height={100}
                alt="Preflight Logo"
                className={preflightJournalLogo()}
                onClick={onLogoClick}
                aria-label="Preflight Logo"
              />
            </>
          )}

          <SettingsContainer>
            <LanguageSwitcher />
            <UserSettings />
          </SettingsContainer>
        </HeaderSectionInner>
      </Container>
    </SectionNew>
  );
};
function LanguageSwitcher() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [, setCookie] = useCookies(['NEXT_LOCALE']);

  const options = [
    { label: 'English', value: 'en' },
    { label: 'Chinese', value: 'cn' },
    { label: 'Japanese', value: 'jp' },
    { label: 'Portuguese (Brazilian)', value: 'br' },
  ];

  // if (!router.query.journalSlug) {
  //   options = [{ label: 'English', value: 'en' }];
  // }

  //Fix build

  const handleLanguageChange = (locale: string) => {
    setOpen(false);
    setCookie('NEXT_LOCALE', locale, { path: '/' });
    router.push({ pathname: router.pathname, query: router.query }, undefined, {
      locale,
    });
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor>
        <Button
          color={'secondary'}
          size={'small'}
          css={{ minWidth: 120 }}
          rightIcon={
            <Icon name={'chevronDown'} variant={'regular'} width="14px" />
          }
          onClick={() => setOpen(!open)}
          aria-label="Language switcher"
        >
          {router.locale === 'en'
            ? 'English'
            : router.locale === 'jp'
            ? 'Japanese'
            : router.locale === 'br'
            ? 'Portuguese (Brazilian)'
            : 'Chinese'}
        </Button>
      </Popover.Anchor>
      <Popover.Popup css={{ minWidth: 120 }} align={'end'}>
        <List>
          {options.map((option, index) => (
            <ListItem key={index}>
              <ListItemButton
                onClick={() => handleLanguageChange(option.value)}
                css={{
                  backgroundColor:
                    router.locale === option.value ? '#e2e8f0' : 'white',
                  fontWeight:
                    router.locale === option.value ? 'bold' : 'normal',
                  cursor: 'pointer',
                }}
                aria-label={`Switch to ${option.label}`}
              >
                <ListItemLeft>{option.label}</ListItemLeft>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover.Popup>
    </Popover.Root>
  );
}

const preflightLogo = css({
  cursor: 'pointer',
  width: 'auto',
  height: '35px',
  display: 'inline',
});
const preflightLogo2 = css({
  cursor: 'pointer',
  width: 'auto',
  height: '45px',
  display: 'inline',
});

const preflightJournalLogo = css({
  cursor: 'pointer',
  width: 'auto',
  maxHeight: '35px',
});

const JournalTitle = styled('div', {
  fontSize: '20px',
  fontStyle: 'italic',
  lineHeight: '31px',
  fontWeight: '500',
  display: 'block',
  position: 'relative',
  height: '25px',

  // eslint-disable-next-line custom/no-hardcoded-colors
  '&::after': {
    content: 'attr(title)',
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: '#fff',
    padding: '5px 10px',
    borderRadius: '4px',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    display: 'none',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.2s, visibility 0s',
  },

  '&:hover::after': {
    opacity: 1,
    visibility: 'visible',
  },
});

const Tooltip = styled('div', {
  position: 'absolute',
  backgroundColor: theme.colors.blue12,
  color: theme.colors.white,
  padding: '5px 10px',
  borderRadius: '4px',
  fontSize: '14px',
  fontFamily: 'IBM Plex Sans',
  top: '100%',
  left: '30%',
  lineHeight: '22px',
  fontWeight: '400',
  fontStyle: 'normal',
  width: '300px',
  zIndex: 99,
});

function truncateTitle(title: string, length: number) {
  if (title.length > length + 2) {
    const text = title.substring(0, length).trim();
    return `${text}...`;
  } else {
    return title;
  }
}
